import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CircleCheck,
  FileCheck2,
  LoaderCircle,
  Send,
  Target,
  Users,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";
import Button from "@/components/primitives/Button";
import type { formSheetsProps } from "@/services/googleSheets/types";

const inputClass = "field";
const textareaClass = "field min-h-[100px] resize-y";

const initialForm: formSheetsProps = {
  razaoSocial: "",
  nomeFantasia: "",
  contatoAdministrativoNome: "",
  contatoAdministrativoCargo: "",
  contatoAdministrativoEmail: "",
  contatoAdministrativoTelefone: "",
  contatoTecnicoNome: "",
  contatoTecnicoCargo: "",
  contatoTecnicoEmail: "",
  contatoTecnicoTelefone: "",
  contatoComercialNome: "",
  contatoComercialCargo: "",
  contatoComercialEmail: "",
  contatoComercialTelefone: "",
  areaAtuacao: "",
  tempoMercado: "",
  segmentos: {
    educacao: false,
    varejo: false,
    agronegocio: false,
    esportivo: false,
    trafego: false,
    corporativo: false,
    industrial: false,
    residencial: false,
    governo: false,
    outros: false,
    outrosDescricao: "",
  },
  experienciaSaaS: "",
  experienciaIA: "",
  experienciaSegEletronica: "",
  produtosServicos: "",
  areaCobertura: "",
  interesseParceira: "",
  equipeTecnicaPropria: "",
  equipeTecnicaComo: "",
  equipeTecnicaQualificacao: "",
  usaHelpDesk: "",
  helpDeskQual: "",
  horarioSuporte: "",
  docContratoSocialChecked: false,
  docCNPJChecked: false,
  docComprovanteEnderecoChecked: false,
  docCertidoesNegativasChecked: false,
  docApresentacaoInstitucionalChecked: false,
  declaracao: false,
  responsavelNome: "",
  responsavelCargo: "",
  responsavelData: "",
  // Preenchidos pela equipe AlertAI na análise interna — não exibidos no formulário público.
  dataRecebimento: "",
  analisadoPor: "",
  statusHomologacao: "",
  observacoes: "",
};

const steps = [
  { title: "Empresa", icon: Building2 },
  { title: "Contatos", icon: Users },
  { title: "Mercado", icon: Target },
  { title: "Capacidade técnica", icon: Wrench },
  { title: "Documentos", icon: FileCheck2 },
] as const;

type ChangeEvt = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

/** Label persistente acima do campo (em vez de depender só do placeholder). */
function Field({
  id,
  label,
  required,
  className,
  children,
}: {
  id?: string;
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-2 block text-[13px] font-medium text-fg/90">
        {label}
        {required && (
          <span className="ml-0.5 text-electric" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

/** Pergunta Sim/Não como par de pílulas — mais rápido de responder que radios soltos. */
function YesNo({
  label,
  name,
  value,
  onChange,
  required,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvt) => void;
  required?: boolean;
}) {
  return (
    <div>
      <p className="mb-2 text-[13px] font-medium text-fg/90">
        {label}
        {required && (
          <span className="ml-0.5 text-electric" aria-hidden="true">
            *
          </span>
        )}
      </p>
      <div className="flex gap-3">
        {(["Sim", "Não"] as const).map((opt) => (
          <label
            key={opt}
            className={cn(
              "flex h-11 flex-1 cursor-pointer items-center justify-center rounded-xl border text-sm font-medium transition-colors sm:flex-none sm:px-8",
              value === opt
                ? "border-electric/60 bg-electric/10 text-fg"
                : "border-line/15 bg-white/[0.03] text-muted hover:border-line/30"
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={onChange}
              required={required}
              className="sr-only"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

/** Tag selecionável para listas de múltipla escolha (segmentos de mercado). */
function CheckboxPill({
  label,
  name,
  checked,
  onChange,
}: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvt) => void;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
        checked
          ? "border-electric/60 bg-electric/10 text-fg"
          : "border-line/15 bg-white/[0.03] text-muted hover:border-line/30"
      )}
    >
      <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" />
      {checked && <Check className="h-3.5 w-3.5 text-electric" aria-hidden="true" />}
      {label}
    </label>
  );
}

/** Linha do checklist de documentação — cartão clicável em vez de checkbox solto. */
function DocRow({
  label,
  name,
  checked,
  onChange,
  optional,
}: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvt) => void;
  optional?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors",
        checked
          ? "border-electric/50 bg-electric/[0.06]"
          : "border-line/12 bg-white/[0.02] hover:border-line/25"
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors",
          checked ? "border-electric bg-electric text-ink" : "border-line/30"
        )}
        aria-hidden="true"
      >
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" />
      <span className="text-sm leading-relaxed text-fg/90">
        {label}
        {optional && <span className="ml-1.5 text-xs text-muted">(Opcional)</span>}
      </span>
    </label>
  );
}

function Stepper({ step }: { step: number }) {
  return (
    <div className="mb-9">
      <div className="mb-4 flex items-center justify-between sm:hidden">
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted">
          Etapa {step + 1} de {steps.length}
        </span>
        <span className="text-xs font-semibold text-electric">{steps[step].title}</span>
      </div>
      <div className="mb-2 h-1 overflow-hidden rounded-full bg-white/[0.06] sm:hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-electric to-brand transition-[width] duration-500"
          style={{ width: `${((step + 1) / steps.length) * 100}%` }}
        />
      </div>

      <ol className="hidden items-center sm:flex">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const state = i < step ? "done" : i === step ? "current" : "upcoming";
          return (
            <li key={s.title} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-2">
                <span
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors",
                    state === "done" && "border-electric bg-electric text-ink",
                    state === "current" && "border-electric bg-electric/15 text-electric",
                    state === "upcoming" && "border-line/20 text-muted"
                  )}
                >
                  {state === "done" ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  )}
                </span>
                <span
                  className={cn(
                    "text-xs font-medium",
                    state === "upcoming" ? "text-muted" : "text-fg"
                  )}
                >
                  {s.title}
                </span>
              </div>
              {i < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "mx-3 h-px flex-1 transition-colors",
                    i < step ? "bg-electric/60" : "bg-line/15"
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const FormReseller = () => {
  const [form, setForm] = React.useState<formSheetsProps>(initialForm);
  const [step, setStep] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const lastStep = steps.length - 1;

  const handleChange = (e: ChangeEvt) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    if (name.startsWith("segmentos.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({
        ...prev,
        segmentos: {
          ...prev.segmentos,
          [key]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const phoneHandler = (name: keyof formSheetsProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, "");
    v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
    v = v.replace(/(\d{5})(\d)/, "$1-$2");
    v = v.slice(0, 15);
    setForm((prev) => ({ ...prev, [name]: v }));
  };

  const goNext = () => {
    if (!formRef.current?.reportValidity()) return;
    setStep((s) => Math.min(s + 1, lastStep));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const resetForm = () => {
    setForm(initialForm);
    setStep(0);
    setSuccess(false);
    setFormError(null);
  };

  const enviarDados = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== lastStep) return;
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/updateSpreadSheetReseler", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const errorText = await res.text();
        setFormError("Erro ao enviar o formulário: " + errorText);
        return;
      }
      const data = await res.json();
      if (data?.error) {
        setFormError("Erro ao enviar o formulário: " + data.error);
        return;
      }
      setSuccess(true);
    } catch (err: unknown) {
      setFormError(
        "Erro ao enviar o formulário: " +
          (err instanceof Error ? err.message : "Erro desconhecido")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const emailInvalid = (e: React.InvalidEvent<HTMLInputElement>) =>
    e.target.setCustomValidity("E-mail inválido");
  const phoneInvalid = (e: React.InvalidEvent<HTMLInputElement>) =>
    e.target.setCustomValidity("Telefone deve estar no formato (99) 99999-9999");
  const clearValidity = (e: React.FormEvent<HTMLInputElement>) =>
    (e.target as HTMLInputElement).setCustomValidity("");

  if (success) {
    return (
      <div className="glass relative flex w-full max-w-6xl flex-col items-center rounded-3xl p-10 text-center shadow-[0_40px_100px_-30px_rgb(0_0_0_/_0.8)] sm:p-16">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ok/15 text-ok">
          <CircleCheck className="h-8 w-8" />
        </span>
        <h3 className="mt-6 text-2xl font-semibold tracking-tight text-fg">
          Cadastro enviado!
        </h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted">
          Recebemos as informações da sua empresa. Nossa equipe vai analisar o
          cadastro e retornar em breve com os próximos passos.
        </p>
        <div className="mt-7">
          <Button variant="secondary" onClick={resetForm}>
            Enviar outro cadastro
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className="glass relative flex w-full max-w-6xl scroll-mt-24 flex-col gap-6 rounded-3xl p-6 shadow-[0_40px_100px_-30px_rgb(0_0_0_/_0.8)] sm:p-9"
      onSubmit={enviarDados}
    >
      <Stepper step={step} />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-5"
        >
          {step === 0 && (
            <>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field id="razaoSocial" label="Razão Social" required>
                  <input
                    id="razaoSocial"
                    className={inputClass}
                    placeholder="Razão social da empresa"
                    name="razaoSocial"
                    value={form.razaoSocial}
                    onChange={handleChange}
                    required
                    autoComplete="organization"
                  />
                </Field>
                <Field id="nomeFantasia" label="Nome Fantasia" required>
                  <input
                    id="nomeFantasia"
                    className={inputClass}
                    placeholder="Nome fantasia"
                    name="nomeFantasia"
                    value={form.nomeFantasia}
                    onChange={handleChange}
                    required
                    autoComplete="organization"
                  />
                </Field>
              </div>
              <Field
                id="areaAtuacao"
                label="Principal área de atuação da sua empresa"
                required
              >
                <textarea
                  id="areaAtuacao"
                  className={textareaClass}
                  placeholder="Descreva brevemente o que a sua empresa faz"
                  name="areaAtuacao"
                  value={form.areaAtuacao}
                  onChange={handleChange}
                  required
                />
              </Field>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field id="tempoMercado" label="Tempo de mercado" required>
                  <input
                    id="tempoMercado"
                    className={inputClass}
                    placeholder="Ex.: 5 anos"
                    name="tempoMercado"
                    value={form.tempoMercado}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <Field id="areaCobertura" label="Área de cobertura geográfica" required>
                  <input
                    id="areaCobertura"
                    className={inputClass}
                    placeholder="Ex.: Grande São Paulo"
                    name="areaCobertura"
                    value={form.areaCobertura}
                    onChange={handleChange}
                    required
                  />
                </Field>
              </div>
            </>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {(
                [
                  { key: "Administrativo", label: "Contato Administrativo/Contratual", prefix: "contatoAdministrativo" },
                  { key: "Tecnico", label: "Contato Técnico", prefix: "contatoTecnico" },
                  { key: "Comercial", label: "Contato Comercial", prefix: "contatoComercial" },
                ] as const
              ).map((c) => (
                <div key={c.key} className="rounded-2xl border border-line/10 p-4">
                  <p className="mb-4 text-sm font-semibold text-fg">{c.label} *</p>
                  <div className="flex flex-col gap-3">
                    <Field id={`${c.prefix}Nome`} label="Nome completo" required>
                      <input
                        id={`${c.prefix}Nome`}
                        className={inputClass}
                        name={`${c.prefix}Nome`}
                        value={form[`${c.prefix}Nome` as keyof formSheetsProps] as string}
                        onChange={handleChange}
                        required
                      />
                    </Field>
                    <Field id={`${c.prefix}Cargo`} label="Cargo" required>
                      <input
                        id={`${c.prefix}Cargo`}
                        className={inputClass}
                        name={`${c.prefix}Cargo`}
                        value={form[`${c.prefix}Cargo` as keyof formSheetsProps] as string}
                        onChange={handleChange}
                        required
                      />
                    </Field>
                    <Field id={`${c.prefix}Email`} label="E-mail" required>
                      <input
                        id={`${c.prefix}Email`}
                        className={inputClass}
                        placeholder="voce@empresa.com.br"
                        type="email"
                        name={`${c.prefix}Email`}
                        value={form[`${c.prefix}Email` as keyof formSheetsProps] as string}
                        onChange={handleChange}
                        required
                        pattern="^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$"
                        onInvalid={emailInvalid}
                        onInput={clearValidity}
                      />
                    </Field>
                    <Field id={`${c.prefix}Telefone`} label="Telefone/Celular" required>
                      <input
                        id={`${c.prefix}Telefone`}
                        className={inputClass}
                        placeholder="(00) 00000-0000"
                        name={`${c.prefix}Telefone`}
                        value={form[`${c.prefix}Telefone` as keyof formSheetsProps] as string}
                        onChange={phoneHandler(`${c.prefix}Telefone` as keyof formSheetsProps)}
                        required
                        pattern="^\(\d{2}\) \d{5}-\d{4}$"
                        onInvalid={phoneInvalid}
                        onInput={clearValidity}
                        maxLength={15}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <>
              <div>
                <p className="mb-3 text-[13px] font-medium text-fg/90">
                  Segmentos de mercado que sua empresa atende principalmente
                  <span className="ml-0.5 text-electric" aria-hidden="true">*</span>
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <CheckboxPill label="Educação" name="segmentos.educacao" checked={form.segmentos.educacao} onChange={handleChange} />
                  <CheckboxPill label="Varejo (Retail)" name="segmentos.varejo" checked={form.segmentos.varejo} onChange={handleChange} />
                  <CheckboxPill label="Agronegócio" name="segmentos.agronegocio" checked={form.segmentos.agronegocio} onChange={handleChange} />
                  <CheckboxPill label="Esportivo" name="segmentos.esportivo" checked={form.segmentos.esportivo} onChange={handleChange} />
                  <CheckboxPill label="Tráfego/Cidades Inteligentes" name="segmentos.trafego" checked={form.segmentos.trafego} onChange={handleChange} />
                  <CheckboxPill label="Corporativo" name="segmentos.corporativo" checked={form.segmentos.corporativo} onChange={handleChange} />
                  <CheckboxPill label="Industrial" name="segmentos.industrial" checked={form.segmentos.industrial} onChange={handleChange} />
                  <CheckboxPill label="Residencial" name="segmentos.residencial" checked={form.segmentos.residencial} onChange={handleChange} />
                  <CheckboxPill label="Governo" name="segmentos.governo" checked={form.segmentos.governo} onChange={handleChange} />
                  <CheckboxPill label="Outros" name="segmentos.outros" checked={form.segmentos.outros} onChange={handleChange} />
                </div>
                <AnimatePresence initial={false}>
                  {form.segmentos.outros && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <input
                        className={cn(inputClass, "mt-3")}
                        placeholder="Especifique o segmento"
                        name="segmentos.outrosDescricao"
                        value={form.segmentos.outrosDescricao}
                        onChange={handleChange}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <YesNo
                  label="Experiência com soluções SaaS?"
                  name="experienciaSaaS"
                  value={form.experienciaSaaS}
                  onChange={handleChange}
                  required
                />
                <YesNo
                  label="Experiência com IA ou análise de vídeo?"
                  name="experienciaIA"
                  value={form.experienciaIA}
                  onChange={handleChange}
                  required
                />
                <YesNo
                  label="Experiência com segurança eletrônica (CFTV, controle de acesso)?"
                  name="experienciaSegEletronica"
                  value={form.experienciaSegEletronica}
                  onChange={handleChange}
                  required
                />
              </div>

              <Field id="produtosServicos" label="Principais produtos/serviços oferecidos hoje">
                <textarea
                  id="produtosServicos"
                  className={textareaClass}
                  placeholder="Opcional"
                  name="produtosServicos"
                  value={form.produtosServicos}
                  onChange={handleChange}
                />
              </Field>
              <Field id="interesseParceira" label="Por que sua empresa tem interesse em se tornar parceira da Alert AI?" required>
                <textarea
                  id="interesseParceira"
                  className={textareaClass}
                  name="interesseParceira"
                  value={form.interesseParceira}
                  onChange={handleChange}
                  required
                />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <YesNo
                label="Sua empresa possui equipe técnica própria para instalações e suporte N1?"
                name="equipeTecnicaPropria"
                value={form.equipeTecnicaPropria}
                onChange={handleChange}
                required
              />
              <AnimatePresence initial={false}>
                {form.equipeTecnicaPropria === "Não" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <Field id="equipeTecnicaComo" label="Como pretende realizar estas atividades?">
                      <input
                        id="equipeTecnicaComo"
                        className={inputClass}
                        name="equipeTecnicaComo"
                        value={form.equipeTecnicaComo}
                        onChange={handleChange}
                      />
                    </Field>
                  </motion.div>
                )}
              </AnimatePresence>
              <Field id="equipeTecnicaQualificacao" label="Qualificação da equipe técnica (certificações, treinamentos)">
                <textarea
                  id="equipeTecnicaQualificacao"
                  className={textareaClass}
                  placeholder="Opcional"
                  name="equipeTecnicaQualificacao"
                  value={form.equipeTecnicaQualificacao}
                  onChange={handleChange}
                />
              </Field>

              <YesNo
                label="Sua empresa utiliza algum sistema de Help Desk?"
                name="usaHelpDesk"
                value={form.usaHelpDesk}
                onChange={handleChange}
                required
              />
              <AnimatePresence initial={false}>
                {form.usaHelpDesk === "Sim" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <Field id="helpDeskQual" label="Qual sistema?">
                      <input
                        id="helpDeskQual"
                        className={inputClass}
                        name="helpDeskQual"
                        value={form.helpDeskQual}
                        onChange={handleChange}
                      />
                    </Field>
                  </motion.div>
                )}
              </AnimatePresence>

              <Field id="horarioSuporte" label="Horário de atendimento do suporte técnico N1" required>
                <input
                  id="horarioSuporte"
                  className={inputClass}
                  placeholder="Ex.: seg. a sex., 8h às 18h"
                  name="horarioSuporte"
                  value={form.horarioSuporte}
                  onChange={handleChange}
                  required
                />
              </Field>
            </>
          )}

          {step === 4 && (
            <>
              <div>
                <p className="mb-3 text-[13px] font-medium text-fg/90">
                  Documentação a anexar no processo de homologação
                </p>
                <div className="flex flex-col gap-2.5">
                  <DocRow
                    label="Cópia do Contrato Social ou Última Alteração Contratual"
                    name="docContratoSocialChecked"
                    checked={form.docContratoSocialChecked}
                    onChange={handleChange}
                  />
                  <DocRow
                    label="Cópia do Cartão CNPJ"
                    name="docCNPJChecked"
                    checked={form.docCNPJChecked}
                    onChange={handleChange}
                  />
                  <DocRow
                    label="Comprovante de Endereço Recente"
                    name="docComprovanteEnderecoChecked"
                    checked={form.docComprovanteEnderecoChecked}
                    onChange={handleChange}
                  />
                  <DocRow
                    label="Certidões Negativas de Débitos (Federal, Estadual, Municipal)"
                    name="docCertidoesNegativasChecked"
                    checked={form.docCertidoesNegativasChecked}
                    onChange={handleChange}
                    optional
                  />
                  <DocRow
                    label="Apresentação Institucional da Empresa"
                    name="docApresentacaoInstitucionalChecked"
                    checked={form.docApresentacaoInstitucionalChecked}
                    onChange={handleChange}
                    optional
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-line/12 bg-white/[0.02] p-4">
                <label className="flex items-start gap-3 text-sm leading-relaxed text-fg/90">
                  <input
                    type="checkbox"
                    name="declaracao"
                    checked={form.declaracao}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-electric"
                    required
                  />
                  Declaro que as informações fornecidas neste formulário são
                  verdadeiras e precisas. Autorizo a AlertAI a verificar as
                  informações e a entrar em contato para dar seguimento ao
                  processo de homologação.
                </label>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Field id="responsavelNome" label="Nome do responsável pelo preenchimento" required>
                  <input
                    id="responsavelNome"
                    className={inputClass}
                    name="responsavelNome"
                    value={form.responsavelNome}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <Field id="responsavelCargo" label="Cargo" required>
                  <input
                    id="responsavelCargo"
                    className={inputClass}
                    name="responsavelCargo"
                    value={form.responsavelCargo}
                    onChange={handleChange}
                    required
                  />
                </Field>
                <Field id="responsavelData" label="Data" required>
                  <input
                    id="responsavelData"
                    className={inputClass}
                    type="date"
                    name="responsavelData"
                    value={form.responsavelData}
                    onChange={handleChange}
                    required
                  />
                </Field>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between gap-3 border-t border-line/10 pt-6">
        {step > 0 ? (
          <button type="button" onClick={goBack} className="btn btn-secondary btn-md">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </button>
        ) : (
          <span />
        )}

        {step < lastStep ? (
          <button type="button" onClick={goNext} className="btn btn-primary btn-md">
            Avançar
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary btn-lg disabled:cursor-wait disabled:opacity-80"
          >
            {submitting ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            {submitting ? "Enviando…" : "Enviar cadastro"}
          </button>
        )}
      </div>

      {formError && (
        <div
          role="alert"
          className="rounded-2xl border border-danger/40 bg-danger/10 p-4 text-sm font-medium text-fg"
        >
          {formError}
        </div>
      )}
    </form>
  );
};

export default FormReseller;
